import VueCookies from 'vue-cookies'
import * as loggers from "@/utils/loggers"
import { useLoadingStore } from '@/stores/loading'

// pinia storeを返す（Worker呼び出しではエラーになるので、undefinedを返す）
const getLoadingStore = () => {
  try {
    const loadingStore = useLoadingStore()
    return loadingStore
  } catch {
    return
  }
}

const defaultBackRoute = { name: 'ConstructionList' }

const maxSearchHistoryCount = 3;

export default {
  namespaced: true,
  state: {
    loading: false, // 使わない
    title: '',
    /** @type {'menu' | 'back'} */
    leftHeaderButton: 'back',
    backRoute: defaultBackRoute,
    talkLinkUrl: null,
    // 現在表示中のURL（route.fullPathだけではなく、表示中にも適宜更新）
    returnKcloudPath: null,
    // ページの表示状態 true: 表示中, false: 非表示
    pageVisible: true,
    /**
     * 検索履歴
     * @type {
    {
      [accountId: string]: {
        photo : string[],
        kokuban : string[],
      }
    }
    } */
    searchHistoryMap_v1: {},
    // 黒板裏表フラグ. false: 表, true: 裏
    kokubanReverseFlg: false,
    requestPassResetReturnRoute: null,
    errorWatcherReady: false,
    zumenViewerUrl: null,
    afterErrorRoute: null,
    photoZoomSize: 2,
  },
  getters: {
    loading: () => {
      return getLoadingStore()?.isLoading || false // piniaに委譲
    },
    title: state => state.title,
    leftHeaderButton: state => state.leftHeaderButton,
    backRoute: state => state.backRoute,
    talkLinkUrl: state => state.talkLinkUrl,
    returnKcloudPath: state => state.returnKcloudPath,
    pageVisible: state => state.pageVisible,
    searchHistory: state => (accountId, dataType) => {
      if (!state.searchHistoryMap_v1[accountId]) return null;
      return state.searchHistoryMap_v1[accountId][dataType]
    },
    kokubanReverseFlg:state => state.kokubanReverseFlg,
    requestPassResetReturnRoute: state => {
      if(!state.requestPassResetReturnRoute) return { name: 'Login' };
      if(!state.requestPassResetReturnRoute.name) return { name: 'Login' };
      return state.requestPassResetReturnRoute;
    },
    errorWatcherReady: state => state.errorWatcherReady,
    zumenViewerUrl: state => state.zumenViewerUrl,
    afterErrorRoute: state => state.afterErrorRoute,
    photoZoomSize: state => state.photoZoomSize,
  },
  mutations: {
    setTitle(state, title){
      state.title = title
    },
    setLeftHeaderButton(state, leftHeaderButton){
      state.leftHeaderButton = leftHeaderButton
    },
    setBackRoute(state, backRoute){
      state.backRoute = backRoute
    },
    setTalkLinkUrl(state, talkLinkUrl){
      state.talkLinkUrl = talkLinkUrl
    },
    setReturnKcloudPath(state, returnKcloudPath){
      state.returnKcloudPath = returnKcloudPath
    },
    setPageVisible(state, pageVisible){
      state.pageVisible = pageVisible
    },
    setSearchHistory(state, { accountId, dataType, modSearchHistory }){
      // アカウント別がない場合
      if(!state.searchHistoryMap_v1[accountId]){
        state.searchHistoryMap_v1[accountId] = {} // 初期登録
      }
      state.searchHistoryMap_v1[accountId][dataType] = modSearchHistory
      state.searchHistoryMap_v1 = Object.assign({}, state.searchHistoryMap_v1, state.searchHistoryMap_v1)
    },
    setKokubanReverseFlg(state, kokubanReverseFlg){
      state.kokubanReverseFlg = kokubanReverseFlg
    },
    setRequestPassResetReturnRoute(state, requestPassResetReturnRoute){
      state.requestPassResetReturnRoute = requestPassResetReturnRoute
    },
    setErrorWatcherReady(state, errorWatcherReady){
      state.errorWatcherReady = errorWatcherReady
    },
    setZumenViewerUrl(state, zumenViewerUrl){
      state.zumenViewerUrl = zumenViewerUrl
    },
    setAfterErrorRoute(state, afterErrorRoute){
      state.afterErrorRoute = afterErrorRoute
    },
    setPhotoZoomSize(state, photoZoomSize){
      state.photoZoomSize = photoZoomSize
    },
  },
  actions: {
    startLoading(){ 
      getLoadingStore()?.__startLoading() // piniaに委譲
    },
    endLoading(){
      getLoadingStore()?.__endLoading() // piniaに委譲
    },
    updateTitle({ commit }, title){ 
      commit('setTitle', title)
    },
    clearTitle({ commit }){ 
      commit('setTitle', '')
    },
    updateLeftHeaderButton({ commit }, leftHeaderButton){ 
      commit('setLeftHeaderButton', leftHeaderButton)
    },
    updateBackRoute({ commit }, backRoute){ 
      commit('setBackRoute', backRoute)
    },
    initBackRoute({ commit }){ 
      commit('setBackRoute', defaultBackRoute)
    },
    clearTalkLinkUrl(context){ 
      context.commit('setTalkLinkUrl', null)
    },
    updateTalkLinkUrl({ commit }, talkLinkUrl){
      commit('setTalkLinkUrl', talkLinkUrl)
    },
    updateReturnKcloudPath({ commit }, returnKcloudPath){
      commit('setReturnKcloudPath', returnKcloudPath)
    },
    updatePageVisible({ commit }, pageVisible){
      commit('setPageVisible', pageVisible)
    },
    addSearchHistory({ getters, commit }, { accountId, dataType, searchWord }){
      if( !accountId ) return;
      if( !searchWord || !searchWord.trim() ){
        return
      }
      const curSearchHistory = getters.searchHistory(accountId, dataType) || []
      const added = [ searchWord.trim(), ...curSearchHistory ]
      const uniq = [...new Set(added)]
      const limited = uniq.slice(0, maxSearchHistoryCount);
      commit('setSearchHistory', { accountId, dataType, modSearchHistory: limited })
    },
    updateKokubanReverseFlg({ commit }, kokubanReverseFlg){ 
      commit('setKokubanReverseFlg', kokubanReverseFlg)
    },
    initKokubanReverseFlg({ commit }){ 
      commit('setKokubanReverseFlg', false)
    },
    updateRequestPassResetReturnRoute({ commit }, requestPassResetReturnRoute){
      commit('setRequestPassResetReturnRoute', requestPassResetReturnRoute)
    },
    async loginCompleted(context, { client_key_prefix }){
      // セッションが作成された場合のアクション
      // 工事ごとのコンテンツ保管場所のクリア後に、初期化
      await context.dispatch('contents/deleteContentsData', null, { root: true })
      await context.dispatch('contents/initializeContentsStorage', null, { root: true })

      const sessionId =VueCookies.get("session_id");
      const dispSessId = (sessionId ? sessionId.substring(0, 8) : '');
      loggers.setSessionId(dispSessId)
      loggers.info(`update sessionId: ${dispSessId}`);
      
      if(client_key_prefix){
        // セッション開始時にサーバーから返ってくるphotoKeyのPrefixで、初期化
        await context.dispatch("itemKeys/updateClientKeyPrefix",
          client_key_prefix, { root: true })
      }
    },
    updateErrorWatcherReady({ commit }, errorWatcherReady){
      commit('setErrorWatcherReady', errorWatcherReady)
    },
    updateZumenViewerUrl({ commit }, zumenViewerUrl){
      commit('setZumenViewerUrl', zumenViewerUrl)
    },
    updateAfterErrorRoute({ commit }, afterErrorRoute){
      commit('setAfterErrorRoute', afterErrorRoute)
    },
    initAfterErrorRoute({ commit }){ 
      commit('setAfterErrorRoute', null)
    },
    updatePhotoZoomSize({ commit }, photoZoomSize){ 
      commit('setPhotoZoomSize', photoZoomSize)
    },
  }
}
